import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {}; // module safety

const BASE_URL = "/api";

type PaymentHandlingData = {
  merchant_id: string;

  monthly_sales: string;
  monthly_credit_usage: string;
  settlement_ratio: string;

  ratio_cash: string;
  ratio_shinpan: string;
  ratio_credit_card: string;

  shinpan_contract: string;
  shinpan_company_1: string;
  shinpan_customer_fee_1: string;
  shinpan_store_fee_1: string;
  shinpan_company_2: string;
  shinpan_customer_fee_2: string;
  shinpan_store_fee_2: string;
  shinpan_company_3: string;
  shinpan_customer_fee_3: string;
  shinpan_store_fee_3: string;
  shinpan_company_4: string;
  shinpan_customer_fee_4: string;
  shinpan_store_fee_4: string;
  shinpan_company_5: string;
  shinpan_customer_fee_5: string;
  shinpan_store_fee_5: string;

  creditcard_contract: string;
  creditcard_company_1: string;
  creditcard_customer_fee_1: string;
  creditcard_store_fee_1: string;
  creditcard_company_2: string;
  creditcard_customer_fee_2: string;
  creditcard_store_fee_2: string;

  service_handling: string;

  is_completed: boolean;
  updated_at?: string;
};

function ReadonlyRow({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "260px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <div style={{ fontWeight: 500, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
        {value && String(value).trim() ? String(value) : <span style={{ color: "#94a3b8" }}>—</span>}
      </div>
    </div>
  );
}

function EditRow({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "260px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          fontWeight: 700,
          color: "#0f172a",
          outline: "none",
          background: "#fff",
        }}
      />
    </div>
  );
}

function SectionTitle({ text }: { text: string }) {
  return (
    <div style={{ padding: "10px 12px", fontWeight: 900, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
      {text}
    </div>
  );
}


function ReadonlyInputBox({
  label,
  value,
  required,
  suffix,
}: {
  label: string;
  value: string | undefined | null;
  required?: boolean;
  suffix?: string;
}) {
  const displayValue = value && String(value).trim() ? String(value) : "—";

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: 44,
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            flex: 1,
            padding: "0 12px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 700,
          }}
        >
          {displayValue}
        </div>

        {suffix && (
          <span
            style={{
              padding: "0 12px",
              height: 44,
              display: "flex",
              alignItems: "center",
              borderLeft: "1px solid #e2e8f0",
              background: "#f1f5f9",
              color: "#334155",
              fontSize: 14,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function ReadonlyCompanyRow({
  idx,
  company,
  customerFee,
  storeFee,
  prefixLabel,
}: {
  idx: number;
  company: string | undefined | null;
  customerFee: string | undefined | null;
  storeFee: string | undefined | null;
  prefixLabel?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)",
        gap: 12,
        marginBottom: 10,
        alignItems: "start",
      }}
    >
      <ReadonlyInputBox label={`${prefixLabel ? prefixLabel : "信販"}会社名（${idx}）`} value={company} />
      <ReadonlyInputBox label="顧客手数料" value={customerFee} suffix="%" />
      <ReadonlyInputBox label="加盟店手数料" value={storeFee} suffix="%" />
    </div>
  );
}

export default function StaffFormViewPaymentHandling() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PaymentHandlingData | null>(null);
  const [error, setError] = useState("");

  const [downloading, setDownloading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [draft, setDraft] = useState({
    monthly_sales: "",
    monthly_credit_usage: "",
    settlement_ratio: "",

    ratio_cash: "",
    ratio_shinpan: "",
    ratio_credit_card: "",

    shinpan_contract: "",
    shinpan_company_1: "",
    shinpan_customer_fee_1: "",
    shinpan_store_fee_1: "",
    shinpan_company_2: "",
    shinpan_customer_fee_2: "",
    shinpan_store_fee_2: "",
    shinpan_company_3: "",
    shinpan_customer_fee_3: "",
    shinpan_store_fee_3: "",
    shinpan_company_4: "",
    shinpan_customer_fee_4: "",
    shinpan_store_fee_4: "",
    shinpan_company_5: "",
    shinpan_customer_fee_5: "",
    shinpan_store_fee_5: "",

    creditcard_contract: "",
    creditcard_company_1: "",
    creditcard_customer_fee_1: "",
    creditcard_store_fee_1: "",
    creditcard_company_2: "",
    creditcard_customer_fee_2: "",
    creditcard_store_fee_2: "",

    service_handling: "",
  });

  const load = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");
    setSaveMsg("");

    try {
      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form3_payment_handling`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const d: PaymentHandlingData | null = json?.data || null;
      setData(d);

      setDraft({
        monthly_sales: d?.monthly_sales || "",
        monthly_credit_usage: d?.monthly_credit_usage || "",
        settlement_ratio: d?.settlement_ratio || "",

        ratio_cash: d?.ratio_cash || "",
        ratio_shinpan: d?.ratio_shinpan || "",
        ratio_credit_card: d?.ratio_credit_card || "",

        shinpan_contract: d?.shinpan_contract || "",
        shinpan_company_1: d?.shinpan_company_1 || "",
        shinpan_customer_fee_1: d?.shinpan_customer_fee_1 || "",
        shinpan_store_fee_1: d?.shinpan_store_fee_1 || "",
        shinpan_company_2: d?.shinpan_company_2 || "",
        shinpan_customer_fee_2: d?.shinpan_customer_fee_2 || "",
        shinpan_store_fee_2: d?.shinpan_store_fee_2 || "",
        shinpan_company_3: d?.shinpan_company_3 || "",
        shinpan_customer_fee_3: d?.shinpan_customer_fee_3 || "",
        shinpan_store_fee_3: d?.shinpan_store_fee_3 || "",
        shinpan_company_4: d?.shinpan_company_4 || "",
        shinpan_customer_fee_4: d?.shinpan_customer_fee_4 || "",
        shinpan_store_fee_4: d?.shinpan_store_fee_4 || "",
        shinpan_company_5: d?.shinpan_company_5 || "",
        shinpan_customer_fee_5: d?.shinpan_customer_fee_5 || "",
        shinpan_store_fee_5: d?.shinpan_store_fee_5 || "",

        creditcard_contract: d?.creditcard_contract || "",
        creditcard_company_1: d?.creditcard_company_1 || "",
        creditcard_customer_fee_1: d?.creditcard_customer_fee_1 || "",
        creditcard_store_fee_1: d?.creditcard_store_fee_1 || "",
        creditcard_company_2: d?.creditcard_company_2 || "",
        creditcard_customer_fee_2: d?.creditcard_customer_fee_2 || "",
        creditcard_store_fee_2: d?.creditcard_store_fee_2 || "",

        service_handling: d?.service_handling || "",
      });

      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム③（決済取扱）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    if (!merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム3_決済取扱_${merchantId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const startEdit = () => {
    setSaveMsg("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    // restore from last loaded data
    setDraft({
      monthly_sales: data?.monthly_sales || "",
      monthly_credit_usage: data?.monthly_credit_usage || "",
      settlement_ratio: data?.settlement_ratio || "",

      ratio_cash: data?.ratio_cash || "",
      ratio_shinpan: data?.ratio_shinpan || "",
      ratio_credit_card: data?.ratio_credit_card || "",

      shinpan_contract: data?.shinpan_contract || "",
      shinpan_company_1: data?.shinpan_company_1 || "",
      shinpan_customer_fee_1: data?.shinpan_customer_fee_1 || "",
      shinpan_store_fee_1: data?.shinpan_store_fee_1 || "",
      shinpan_company_2: data?.shinpan_company_2 || "",
      shinpan_customer_fee_2: data?.shinpan_customer_fee_2 || "",
      shinpan_store_fee_2: data?.shinpan_store_fee_2 || "",
      shinpan_company_3: data?.shinpan_company_3 || "",
      shinpan_customer_fee_3: data?.shinpan_customer_fee_3 || "",
      shinpan_store_fee_3: data?.shinpan_store_fee_3 || "",
      shinpan_company_4: data?.shinpan_company_4 || "",
      shinpan_customer_fee_4: data?.shinpan_customer_fee_4 || "",
      shinpan_store_fee_4: data?.shinpan_store_fee_4 || "",
      shinpan_company_5: data?.shinpan_company_5 || "",
      shinpan_customer_fee_5: data?.shinpan_customer_fee_5 || "",
      shinpan_store_fee_5: data?.shinpan_store_fee_5 || "",

      creditcard_contract: data?.creditcard_contract || "",
      creditcard_company_1: data?.creditcard_company_1 || "",
      creditcard_customer_fee_1: data?.creditcard_customer_fee_1 || "",
      creditcard_store_fee_1: data?.creditcard_store_fee_1 || "",
      creditcard_company_2: data?.creditcard_company_2 || "",
      creditcard_customer_fee_2: data?.creditcard_customer_fee_2 || "",
      creditcard_store_fee_2: data?.creditcard_store_fee_2 || "",

      service_handling: data?.service_handling || "",
    });
    setIsEditing(false);
    setSaveMsg("");
  };

  const saveEdit = async () => {
    if (!merchantId) return;

    try {
      setSaving(true);
      setSaveMsg("");

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form3_payment_handling`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.detail || "保存に失敗しました。");

      setSaveMsg("保存しました。");
      setIsEditing(false);
      await load();
    } catch (e: any) {
      setSaveMsg(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffLayout title="フォーム③ 決済取扱（確認）" active="progress">
      {/* Top buttons */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        {!isEditing ? (
          <button
            onClick={startEdit}
            disabled={loading || !!error || !data}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #0f172a",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 900,
              cursor: "pointer",
              opacity: loading || !!error || !data ? 0.6 : 1,
            }}
            title={!data ? "データが存在しないため編集できません" : ""}
          >
            編集
          </button>
        ) : (
          <>
            <button
              onClick={saveEdit}
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #16a34a",
                background: "#16a34a",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>

            <button
              onClick={cancelEdit}
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#0f172a",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              キャンセル
            </button>
          </>
        )}
      </div>

      {saveMsg && (
        <div
          style={{
            marginBottom: 10,
            background: saveMsg.includes("失敗") ? "#fff1f2" : "#ecfdf5",
            border: saveMsg.includes("失敗") ? "1px solid #fecdd3" : "1px solid #bbf7d0",
            color: saveMsg.includes("失敗") ? "#b91c1c" : "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {saveMsg}
        </div>
      )}

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          {/* status card */}
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data?.is_completed ? "#166534" : "#9a3412" }}>
              {data?.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data?.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
            {isEditing && <div style={{ marginTop: 8, color: "#0f172a", fontWeight: 900 }}>※ スタッフ編集モード</div>}
          </div>

          {/* table */}
<div style={{ border: "1px solid #e2e8f0", borderRadius: 14, background: "#fff", overflow: "hidden" }}>
  <SectionTitle text={isEditing ? "入力内容（編集）" : "入力内容（読み取り専用）"} />

  {isEditing ? (
    <>
      <SectionTitle text="基本情報" />
      <EditRow label="月間売上高（百万円）" value={draft.monthly_sales} onChange={(v) => setDraft((p) => ({ ...p, monthly_sales: v }))} />
      <EditRow label="月間信販利用額（百万円）" value={draft.monthly_credit_usage} onChange={(v) => setDraft((p) => ({ ...p, monthly_credit_usage: v }))} />
      <EditRow label="決済比率（%）" value={draft.settlement_ratio} onChange={(v) => setDraft((p) => ({ ...p, settlement_ratio: v }))} />

      <SectionTitle text="比率（任意）" />
      <EditRow label="現金（%）" value={draft.ratio_cash} onChange={(v) => setDraft((p) => ({ ...p, ratio_cash: v }))} />
      <EditRow label="信販（%）" value={draft.ratio_shinpan} onChange={(v) => setDraft((p) => ({ ...p, ratio_shinpan: v }))} />
      <EditRow label="クレジットカード（%）" value={draft.ratio_credit_card} onChange={(v) => setDraft((p) => ({ ...p, ratio_credit_card: v }))} />

      <SectionTitle text="信販契約" />
      <EditRow label="信販契約（有 / 無）" value={draft.shinpan_contract} onChange={(v) => setDraft((p) => ({ ...p, shinpan_contract: v }))} />
      <EditRow label="信販会社①" value={draft.shinpan_company_1} onChange={(v) => setDraft((p) => ({ ...p, shinpan_company_1: v }))} />
      <EditRow label="お客様手数料①" value={draft.shinpan_customer_fee_1} onChange={(v) => setDraft((p) => ({ ...p, shinpan_customer_fee_1: v }))} />
      <EditRow label="加盟店手数料①" value={draft.shinpan_store_fee_1} onChange={(v) => setDraft((p) => ({ ...p, shinpan_store_fee_1: v }))} />
      <EditRow label="信販会社②" value={draft.shinpan_company_2} onChange={(v) => setDraft((p) => ({ ...p, shinpan_company_2: v }))} />
      <EditRow label="お客様手数料②" value={draft.shinpan_customer_fee_2} onChange={(v) => setDraft((p) => ({ ...p, shinpan_customer_fee_2: v }))} />
      <EditRow label="加盟店手数料②" value={draft.shinpan_store_fee_2} onChange={(v) => setDraft((p) => ({ ...p, shinpan_store_fee_2: v }))} />
      <EditRow label="信販会社③" value={draft.shinpan_company_3} onChange={(v) => setDraft((p) => ({ ...p, shinpan_company_3: v }))} />
      <EditRow label="お客様手数料③" value={draft.shinpan_customer_fee_3} onChange={(v) => setDraft((p) => ({ ...p, shinpan_customer_fee_3: v }))} />
      <EditRow label="加盟店手数料③" value={draft.shinpan_store_fee_3} onChange={(v) => setDraft((p) => ({ ...p, shinpan_store_fee_3: v }))} />
      <EditRow label="信販会社④" value={draft.shinpan_company_4} onChange={(v) => setDraft((p) => ({ ...p, shinpan_company_4: v }))} />
      <EditRow label="お客様手数料④" value={draft.shinpan_customer_fee_4} onChange={(v) => setDraft((p) => ({ ...p, shinpan_customer_fee_4: v }))} />
      <EditRow label="加盟店手数料④" value={draft.shinpan_store_fee_4} onChange={(v) => setDraft((p) => ({ ...p, shinpan_store_fee_4: v }))} />
      <EditRow label="信販会社⑤" value={draft.shinpan_company_5} onChange={(v) => setDraft((p) => ({ ...p, shinpan_company_5: v }))} />
      <EditRow label="お客様手数料⑤" value={draft.shinpan_customer_fee_5} onChange={(v) => setDraft((p) => ({ ...p, shinpan_customer_fee_5: v }))} />
      <EditRow label="加盟店手数料⑤" value={draft.shinpan_store_fee_5} onChange={(v) => setDraft((p) => ({ ...p, shinpan_store_fee_5: v }))} />

      <SectionTitle text="クレジットカード契約" />
      <EditRow label="クレカ契約（有 / 無）" value={draft.creditcard_contract} onChange={(v) => setDraft((p) => ({ ...p, creditcard_contract: v }))} />
      <EditRow label="カード会社①" value={draft.creditcard_company_1} onChange={(v) => setDraft((p) => ({ ...p, creditcard_company_1: v }))} />
      <EditRow label="お客様手数料①" value={draft.creditcard_customer_fee_1} onChange={(v) => setDraft((p) => ({ ...p, creditcard_customer_fee_1: v }))} />
      <EditRow label="加盟店手数料①" value={draft.creditcard_store_fee_1} onChange={(v) => setDraft((p) => ({ ...p, creditcard_store_fee_1: v }))} />
      <EditRow label="カード会社②" value={draft.creditcard_company_2} onChange={(v) => setDraft((p) => ({ ...p, creditcard_company_2: v }))} />
      <EditRow label="お客様手数料②" value={draft.creditcard_customer_fee_2} onChange={(v) => setDraft((p) => ({ ...p, creditcard_customer_fee_2: v }))} />
      <EditRow label="加盟店手数料②" value={draft.creditcard_store_fee_2} onChange={(v) => setDraft((p) => ({ ...p, creditcard_store_fee_2: v }))} />

      <SectionTitle text="その他" />
      <EditRow label="サービス取扱（可 / 不可）" value={draft.service_handling} onChange={(v) => setDraft((p) => ({ ...p, service_handling: v }))} />
    </>
  ) : (
    <div style={{ padding: 12, display: "grid", gap: 18 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
          gap: 20,
          alignItems: "start",
        }}
      >
        <ReadonlyInputBox label="月間売上高" value={data?.monthly_sales} required suffix="万円" />
        <ReadonlyInputBox label="月間信販利用額" value={data?.monthly_credit_usage} required suffix="万円" />
      </div>

      <div style={{ border: "1px solid #cbd5e1", borderRadius: 14, padding: 16, background: "#ffffff" }}>
        <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 12, color: "#0f172a" }}>
          決済比率
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            gap: 20,
            alignItems: "start",
          }}
        >
          <ReadonlyInputBox label="現金" value={data?.ratio_cash} suffix="%" />
          <ReadonlyInputBox label="信販" value={data?.ratio_shinpan} suffix="%" />
          <ReadonlyInputBox label="クレジットカード" value={data?.ratio_credit_card} suffix="%" />
        </div>
      </div>

      <ReadonlyInputBox label="ご利用信販会社" value={data?.shinpan_contract} required />

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 16, padding: 16, background: "#ffffff" }}>
        <div style={{ fontWeight: 900, marginBottom: 6 }}>ご利用信販会社（最大5社）</div>
        <div style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>
          「信販会社が有」の場合に入力されています（社名・顧客手数料・加盟店手数料）
        </div>

        <ReadonlyCompanyRow idx={1} company={data?.shinpan_company_1} customerFee={data?.shinpan_customer_fee_1} storeFee={data?.shinpan_store_fee_1} />
        <ReadonlyCompanyRow idx={2} company={data?.shinpan_company_2} customerFee={data?.shinpan_customer_fee_2} storeFee={data?.shinpan_store_fee_2} />
        <ReadonlyCompanyRow idx={3} company={data?.shinpan_company_3} customerFee={data?.shinpan_customer_fee_3} storeFee={data?.shinpan_store_fee_3} />
        <ReadonlyCompanyRow idx={4} company={data?.shinpan_company_4} customerFee={data?.shinpan_customer_fee_4} storeFee={data?.shinpan_store_fee_4} />
        <ReadonlyCompanyRow idx={5} company={data?.shinpan_company_5} customerFee={data?.shinpan_customer_fee_5} storeFee={data?.shinpan_store_fee_5} />
      </div>

      <ReadonlyInputBox label="ご利用クレジットカード会社" value={data?.creditcard_contract} required />
      <ReadonlyInputBox label="役務取扱" value={data?.service_handling} />

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 16, padding: 16, background: "#ffffff" }}>
        <div style={{ fontWeight: 900, marginBottom: 6 }}>ご利用クレジットカード会社（最大2社）</div>
        <div style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>
          「クレジットカード会社が有」の場合に入力されています（社名・顧客手数料・加盟店手数料）
        </div>

        <ReadonlyCompanyRow idx={1} prefixLabel="クレジットカード" company={data?.creditcard_company_1} customerFee={data?.creditcard_customer_fee_1} storeFee={data?.creditcard_store_fee_1} />
        <ReadonlyCompanyRow idx={2} prefixLabel="クレジットカード" company={data?.creditcard_company_2} customerFee={data?.creditcard_customer_fee_2} storeFee={data?.creditcard_store_fee_2} />
      </div>
    </div>
  )}
</div>
  </div>
      )}
    </StaffLayout>
  );
}
